import {api} from "@/shared/api";

import {RecordingStatusPanel, RecordingStatusPanelConfig} from "@/shared/recording/recordingStatusPanel";
import {DriveLogInfoPanel} from "./driveLogInfoPanel";
import {Combobox} from "@/shared/ui/combobox";
import {isDriveLogDateTimeChangeAllowed} from "@/shared/securityHelper";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {getISOZonedDateTime, parseDateTimeZone} from "@/shared/utils/datefunctions";
import {Car, DriveLog, DriveLogCategory, DriveLogStatus, ErrorResponse, RunningDriveLog} from "@/shared/timr";
import {promiseFailNow} from "@/shared/utils/promise";

export interface DriveLogStatusPanelConfig extends RecordingStatusPanelConfig {
	cars: Car[];
	driveLogCategories: DriveLogCategory[];
}

export class DriveLogStatusPanel extends RecordingStatusPanel<DriveLogStatus, RunningDriveLog, DriveLog> {

	declare private driveLogInfoPanel: DriveLogInfoPanel;
	declare private carCombobox: Combobox<Car>;
	declare private categoryCombobox: Combobox<DriveLogCategory>;

	constructor(public config: DriveLogStatusPanelConfig) {
		super(config);
	}

	public updateStatus(recordingStatus: DriveLogStatus, forceUpdate = true) {
		this.carCombobox.updateItems(recordingStatus.carList);
		super.updateStatus(recordingStatus, forceUpdate);
	}

	protected init() {
		super.init();

		this.config.panel.find(".startMileage, .endMileage").focus(e => {
			this.currentFocusedField = $(e.target).attr("name");
		}).blur(() => {
			this.currentFocusedField = null;
		});
		this.config.panel.find(".startMileage").on("input", e => {
			const input = $(e.target);
			if ($.inArray(input.attr("name"), this.changedFields) === -1) {
				this.changedFields[this.changedFields.length] = input.attr("name");
			}
			this.queueUpdate();
		});
		this.config.panel.find(".endMileage").on("input", e => {
			const input = $(e.target);
			if ($.inArray(input.attr("name"), this.changedFields) === -1) {
				this.changedFields[this.changedFields.length] = input.attr("name");
			}
		});
		this.config.panel.find(".route, .purpose, .visited").on("keyup", () => {
			this.queueUpdate();
		});

		this.carCombobox = new Combobox({
			items: this.config.cars,
			formatter: (car) => {
				return `${car.plate} - ${car.name}`;
			},
			idElement: $("#driveLogStatusCarId"),
			filterElement: $("#driveLogStatusCarFilter"),
			caretElement: $("#driveLogStatusCarCaret"),
			clearable: false
		});
		$(this.carCombobox).on(Combobox.EVENT_CHANGED, (e, car: Car) => {
			this.selectCar(car);
		});

		this.categoryCombobox = new Combobox({
			items: this.config.driveLogCategories,
			formatter: category => category.name,
			idElement: $("#driveLogStatusCategoryId"),
			filterElement: $("#driveLogStatusCategoryFilter"),
			caretElement: $("#driveLogStatusCategoryCaret"),
			clearable: false
		});
		$(this.categoryCombobox).on(Combobox.EVENT_CHANGED, (e, category: DriveLogCategory) => {
			this.selectCategory(category);
		});

		this.driveLogInfoPanel = new DriveLogInfoPanel($("#driveLogInfoPanel"));
	}

	protected pluckRunningRecording(recordingStatus: DriveLogStatus | null): RunningDriveLog | null {
		return recordingStatus ? recordingStatus.runningDriveLog : null;
	}

	protected pluckLastRecording(recordingStatus?: DriveLogStatus | null): DriveLog | null {
		return recordingStatus ? recordingStatus.lastDriveLog : null;
	}

	protected pluckRecordingCategory(): string {
		return "Drive Log";
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isDriveLogDateTimeChangeAllowed();
	}

	protected sendUpdate(): JQuery.jqXHR<DriveLogStatus> {
		const startDate = $("#driveLogStatusStartDate").val() as string;
		const startTime = $("#driveLogStatusStartTime").val() as string || "0";
		const startTimeZone = $("#driveLogStatusStartTimeZone").val() as string;
		const startDateTime = parseDateTimeZone(startDate, startTime, startTimeZone);
		$("#driveLogStatusStartDateTime").val(getISOZonedDateTime(startDateTime));

		const endDate = $("#driveLogStatusEndDate").val() as string;
		const endTime = $("#driveLogStatusEndTime").val() as string;
		const endTimeZone = $("#driveLogStatusEndTimeZone").val() as string;
		const endDateTime = parseDateTimeZone(endDate, endTime, endTimeZone);
		$("#driveLogStatusEndDateTime").val(getISOZonedDateTime(endDateTime));
		return api("recording/updateDriveLogRecording.json")
			.postForm($("#driveLogStatusForm"))
			.done((data: DriveLogStatus) => {
				this.changedFields = $.grep(this.changedFields, (value) => {
					return value !== "startDate" && value !== "startTime" && value !== "startTimeZone";
				});
				$("#driveLogStatusVersion").val(data.runningDriveLog.version);
				this.updateStatus(data);
			});
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddDriveLogAllowed()) {
			BootstrapModal.info({
				title: "account.expired.drivelog.title",
				message: "account.expired.drivelog",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected startRecording(): JQuery.jqXHR<DriveLogStatus> {
		Analytics.trackEvent("Drive Log - Start", "Drive Log");
		this.updateStartTime(this.currentStatus);

		const form = $("#driveLogStatusForm");
		FormUtil.removeAllFeedback(form);
		const params = form.serialize() + "&" + $.param({
			timestamp: Date.now()
		});
		return api("recording/startDriveLogRecording.json")
			.post(params)
			.done((data) => {
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.started"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected startPauseRecording(): JQuery.jqXHR<DriveLogStatus> {
		// Not implemented for drive log
		return promiseFailNow() as JQuery.jqXHR<DriveLogStatus>;
	}

	protected startResumeRecording(): JQuery.jqXHR<DriveLogStatus> {
		// Not implemented for drive log
		return promiseFailNow() as JQuery.jqXHR<DriveLogStatus>;
	}

	protected startSwitchRecording(): JQuery.jqXHR<DriveLogStatus> {
		// Not implemented for drive log
		return promiseFailNow() as JQuery.jqXHR<DriveLogStatus>;
	}

	protected startQuickSwitchRecording(): JQuery.jqXHR<DriveLogStatus> {
		// Not implemented for drive log
		return promiseFailNow() as JQuery.jqXHR<DriveLogStatus>;
	}

	protected stopRecording(): JQuery.jqXHR<DriveLogStatus> {
		Analytics.trackEvent("Drive Log - Stop", "Drive Log");
		this.updateEndTime(this.currentStatus);

		const form = $("#driveLogStatusForm");
		FormUtil.removeAllFeedback(form);
		const params = form.serialize() + "&" + $.param({
			timestamp: Date.now()
		});
		return api("recording/stopDriveLogRecording.json")
			.post(params)
			.done((data) => {
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.stopped"]);
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected deleteRecording(): JQuery.jqXHR<DriveLogStatus> {
		Analytics.trackEvent("Drive Log - Delete", "Drive Log");
		const form = $("#driveLogStatusForm");

		return api("recording/deleteDriveLogRecording.json")
			.post({
				recordId: this.config.panel.find(".recordId").val()
			})
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.deleted"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.code === "E0200") {
					this.fetchRecordingStatus(true);
				}
			});
	}

	protected updateRecordingInfoPanel(recording: RunningDriveLog | null, running: boolean, forceUpdate = true) {
		const car = recording && recording.car ? recording.car : this.getSelectedCar();
		if (car) {
			this.driveLogInfoPanel.update(car);
		}
	}

	protected getSelectedCar(): Car {
		return this.carCombobox.getSelectedItem();
	}

	protected clearFields() {
		super.clearFields();

		this.config.panel.find(".route").val("");
		this.config.panel.find(".purpose").val("");
		this.config.panel.find(".visited").val("");
		this.config.panel.find(".business").prop("checked", false);
	}

	protected updateFieldsWithRunningRecording(runningRecording: RunningDriveLog) {
		super.updateFieldsWithRunningRecording(runningRecording);

		this.config.panel.find(".route").val(runningRecording.route);
		this.config.panel.find(".purpose").val(runningRecording.purpose);
		this.config.panel.find(".visited").val(runningRecording.visited);
	}

	protected updateFields(recordingStatus: DriveLogStatus) {
		super.updateFields(recordingStatus);

		if (recordingStatus.runningDriveLog) {
			if (this.notChanged("car")) {
				this.carCombobox.setSelectedItemById(recordingStatus.runningDriveLog.car.id, false, false);
			}
			if (this.notChanged("category")) {
				this.categoryCombobox.setSelectedItemById(recordingStatus.runningDriveLog.category.id, false, false);
			}
			if (this.notChanged("startMileage") && this.notFocused("startMileage")) {
				this.config.panel.find(".startMileage").val(recordingStatus.runningDriveLog.startMileage);
			}
			if (this.notChanged("endMileage") && this.notFocused("endMileage")) {
				if (recordingStatus.runningDriveLog && recordingStatus.runningDriveLog.endMileage) {
					this.config.panel.find(".endMileage").val(recordingStatus.runningDriveLog.endMileage);
				} else {
					this.config.panel.find(".endMileage").val(this.config.panel.find(".startMileage").val());
				}
			}
		} else if (recordingStatus.lastDriveLog) {
			if (this.notChanged("car")) {
				this.carCombobox.setSelectedItemById(recordingStatus.lastDriveLog.car.id);
			}
			if (this.notChanged("startMileage") && this.notFocused("startMileage")) {
				this.config.panel.find(".startMileage").val(this.getEndOfLastDriveForSelectedCar());
			}
			if (recordingStatus.lastDriveLog.category) {
				if (this.notChanged("category")) {
					this.categoryCombobox.setSelectedItemById(recordingStatus.lastDriveLog.category.id);
				}
			}
		}
		this.updatePanelBorderColor();
	}

	protected updatePanelBorderColor() {
		const panelBody = this.config.panel.find(".panel-body");
		const icon = this.config.panel.find(".recording-status-indicator");

		const category = this.categoryCombobox.getSelectedItem();
		if (this.pluckRunningRecording(this.currentStatus) && category?.business != null) {
			const business = category.business;

			icon.toggleClass("icon-BUSINESS", business);
			icon.toggleClass("icon-PRIVATE", !business);

			panelBody.toggleClass("border-BUSINESS", business);
			panelBody.toggleClass("border-PRIVATE", !business);
		} else {
			panelBody.removeClass("border-BUSINESS").removeClass("border-PRIVATE");
			icon.removeClass("icon-BUSINESS").removeClass("icon-PRIVATE");
		}
	}

	protected updateButtons(runningRecording: RunningDriveLog | null) {
		super.updateButtons(runningRecording);

		this.carCombobox.setDisabled(!!runningRecording);
	}

	protected onStatusUpdated(recordingStatus: DriveLogStatus) {
		super.onStatusUpdated(recordingStatus);
		statusElementsHolder.updateRunningDriveLog(this.pluckRunningRecording(recordingStatus));
	}

	private selectCar(car: Car) {
		if ($.inArray("car", this.changedFields) === -1) {
			this.changedFields[this.changedFields.length] = "car";
		}
		this.config.panel.find(".startMileage").val(car ? car.endOfLastDrive : 0);
		this.driveLogInfoPanel.update(car);
		this.queueUpdate();
	}

	private selectCategory(category: DriveLogCategory) {
		if ($.inArray("category", this.changedFields) === -1) {
			this.changedFields[this.changedFields.length] = "category";
		}
		this.updatePanelBorderColor();
		this.queueUpdate();
	}

	private getEndOfLastDriveForSelectedCar(): number {
		const selectedCar = this.getSelectedCar();
		return selectedCar ? selectedCar.endOfLastDrive : 0;
	}
}
